import {
  Backdrop,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import { t } from "i18next";
import React from "react";
import { useSelector } from "react-redux";
import CorePagination from "../CorePagination/CorePagination";

const CoreTableEnhanced = ({
  rows, // ARRAY degli elementi della tabella
  columns, // ARRAY delle intestazioni delle colonne della tabella
  button, // BOOLEANO se vuoi bottoni nella tabella
  actions, // ARRAY di bottoni della tabella
  isLoading,
  page = false,
  setPage,
  totalPages,
  orderDirection = "asc",
  setOrderDirection,
  valueToOrderBy,
  setValueToOrderBy,
  idsSort = true, // Default to true per la nuova struttura
  collapse,
  openRows,
  emptyMessage = "Nessun elemento", // Messaggio personalizzabile per tabella vuota
  showPagination = true, // Controllo visibilità paginazione
  stickyHeader = false, // Header fisso durante scroll
  maxHeight, // Altezza massima per scroll
}) => {
  const colorPrimary = useSelector((state) => state.app.primaryColor);

  const handleRequestSort = (property) => {
    const isAscending = valueToOrderBy === property && orderDirection === "asc";
    setValueToOrderBy(property);
    setOrderDirection(isAscending ? "desc" : "asc");
  };

  // Funzione per ottenere il valore della cella
  const getCellValue = (row, header) => {
    if (idsSort) {
      return row[header.id] || "";
    }
    return row[header] || "";
  };

  // Funzione per ottenere la chiave di ordinamento
  const getSortKey = (header) => {
    return idsSort ? header.id : header;
  };

  // Funzione per ottenere il label dell'header
  const getHeaderLabel = (header) => {
    return idsSort ? header.label : header;
  };

  // Validazione props
  if (!rows || !columns) {
    console.warn("CoreTableEnhanced: rows e columns sono obbligatori");
    return null;
  }

  return (
    <div className="w-full p-4">
      <TableContainer
        sx={{
          position: "relative",
          ...(maxHeight && { maxHeight }),
        }}
      >
        <Backdrop
          className="backdrop-animation"
          sx={{ position: "absolute", zIndex: 1 }}
          open={isLoading}
        >
          <CircularProgress style={{ color: colorPrimary }} />
        </Backdrop>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size="medium"
          stickyHeader={stickyHeader}
        >
          <TableHead>
            <TableRow>
              {columns.map((header, index) => (
                <TableCell key={index} className="min-w-48">
                  <TableSortLabel
                    disabled={
                      !setOrderDirection ||
                      !setValueToOrderBy ||
                      (idsSort && Array.isArray(idsSort)
                        ? !idsSort.includes(header.id)
                        : false)
                    }
                    active={valueToOrderBy === getSortKey(header)}
                    direction={orderDirection}
                    onClick={() => handleRequestSort(getSortKey(header))}
                  >
                    {getHeaderLabel(header)}
                  </TableSortLabel>
                </TableCell>
              ))}
              {button && <TableCell>{t("Actions")}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <React.Fragment key={row.id || index}>
                <TableRow
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  className="hover:shadow-md"
                  style={row.style}
                >
                  {columns.map((header, cellIndex) => (
                    <TableCell key={cellIndex} sx={{ height: "73px" }}>
                      {getCellValue(row, header)}
                    </TableCell>
                  ))}
                  {button && (
                    <TableCell className="min-w-48">
                      {actions?.map((action, actionIndex) => {
                        const isDisabled = action.disabled?.(row);

                        // If button is disabled, wrap it in a span for the tooltip
                        if (isDisabled) {
                          return (
                            <Tooltip title={action.tooltip} key={actionIndex}>
                              <span>
                                <IconButton
                                  className="w-10 h-10"
                                  onClick={() => action.onClick(row)}
                                  disabled={true}
                                >
                                  {action.icon}
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        }

                        // If button is not disabled, use normal structure
                        return (
                          <Tooltip title={action.tooltip} key={actionIndex}>
                            <IconButton
                              className="w-10 h-10"
                              onClick={() => action.onClick(row)}
                              disabled={false}
                            >
                              {action.icon}
                            </IconButton>
                          </Tooltip>
                        );
                      })}
                    </TableCell>
                  )}
                </TableRow>
                {collapse && openRows?.[row.id] && (
                  <TableRow>
                    <TableCell
                      className="p-0"
                      colSpan={columns.length + (button ? 1 : 0)}
                      sx={{ paddingBottom: 0, paddingTop: 0 }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          padding: "0px",
                          boxSizing: "border-box",
                        }}
                      >
                        <Table size="small" sx={{ width: "100%" }}>
                          <TableBody className="bg-zinc-50">
                            {row.collapse?.map((el, idx) => (
                              <TableRow key={idx}>
                                {columns.map((header, cellIndex) => (
                                  <TableCell key={cellIndex}>
                                    {getCellValue(el, header)}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                            {(!row.collapse || row.collapse.length === 0) && (
                              <TableRow>
                                <TableCell
                                  colSpan={columns.length}
                                  className="text-center"
                                >
                                  <div className="h-16 w-full flex justify-center items-center text-gray">
                                    {t("NotElement")}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && (
          <div className="h-16 w-full flex justify-center items-center text-gray">
            {emptyMessage}
          </div>
        )}
        <Divider />
        {page && showPagination && (
          <div className="w-full flex justify-center">
            <CorePagination
              page={page}
              setPage={setPage}
              totalPages={totalPages}
            />
          </div>
        )}
      </TableContainer>
    </div>
  );
};

export default CoreTableEnhanced;
